import React, { useCallback, useMemo, useState } from 'react';
import { Box, Grid, SearchInput, useDebounce, VirtualTable } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import DateRangeSelector from '../DateRangeSelector';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent, useShowAppliedSettingsSnackbar } from '../hooks';
import InvoiceIDInput from '../InvoiceIDInput';
import PlaceholderBase from '../PlaceholderBase';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import ReportTableWrapper from '../ReportTable/ReportTableWrapper';
import ToggleSSNVisible from '../ToggleSSNVisible';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { useReplenishmentInvoiceReportExportCSVQuery, useReplenishmentInvoiceReportQuery } from './queries';
import { ReplenishmentInvoiceReportTableParams } from './ReplenishmentInvoiceReport.types';

interface Props extends ReportTableBaseProps {
  filtersValues: ReplenishmentInvoiceReportTableParams;
  onSetFilterValue: FilterSetters<ReplenishmentInvoiceReportTableParams>;
}

const ReplenishmentInvoiceReportTable: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues);
  const [isAutoFocus, setAutoFocus] = useState(false);
  const debouncedSearch = useDebounce(filtersValues.searchString);
  const {
    isHasNextPage,
    total,
    isLoading,
    headers,
    nodes,
    isExportDisabled,
    lastTimeUpdateDate,
    columnWidths,
    onChangeHeaderSize,
    handleLoadMore,
  } = useReplenishmentInvoiceReportQuery({
    ...filtersValues,
    searchString: debouncedSearch,
    isPreviewMode,
  });
  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }

    if (!isLoading && !total) {
      if (filtersValues.searchString.trim()) {
        return (
          <PlaceholderBase isLarge>
            <PlaceholderBase.Search />
          </PlaceholderBase>
        );
      }
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }
    return (
      <VirtualTable
        testId="report-table"
        height={isPreviewMode ? 'calc(80vh - 270px)' : 'calc(80vh - 160px)'}
        isLoading={isLoading}
        onResizeHeaders={onChangeHeaderSize}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        columnSizes={columnWidths}
        isIndexMode={true}
        headers={headers}
        items={nodes}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersValues.employerIds,
    isPreviewMode,
    onChangeHeaderSize,
    columnWidths,
    filtersValues.employerId,
    filtersValues.searchString,
    handleLoadMore,
    headers,
    isHasNextPage,
    isLoading,
    nodes,
    total,
  ]);
  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useReplenishmentInvoiceReportExportCSVQuery(filtersValues);

  const handleShowAppliedFilterSnackbar = useShowAppliedSettingsSnackbar();
  const handleToggleSSN = useCallback(
    (value: boolean) => {
      onSetFilterValue.isSSNShowed(value);
      handleShowAppliedFilterSnackbar();
    },
    [handleShowAppliedFilterSnackbar, onSetFilterValue]
  );

  return (
    <Box direction="column">
      <TopButtonPortalWrapper
        isPreviewMode={isPreviewMode}
        infoNode={
          <ReportLastUpdateDate
            isWideMode
            isLoading={isLoading}
            key={`${filtersValues.employerId}`}
            value={lastTimeUpdateDate}
          />
        }
        buttonNode={
          <Box direction="row">
            <AddScheduledReportButton
              employerId={filtersValues.employerId}
              type={ReportTypeKeys.ReplenishmentInvoice}
              tab={ReportTab.Report}
            />
            <Box margin={{ left: 'spacing12' }}>
              <ExportButton
                isLoading={isExportLoading}
                onClick={handleExportCSV}
                disabled={!isEmployerIsPresent || isExportDisabled}
                text="Download Report (CSV)"
              />
            </Box>
          </Box>
        }
      >
        <ReportTableWrapper
          total={total}
          isLoading={isLoading}
          isPreviewMode={isPreviewMode}
          headerChildren={
            <ToggleSSNVisible
              disabled={!isEmployerIsPresent}
              value={filtersValues.isSSNShowed}
              onChangeValue={handleToggleSSN}
            />
          }
          isSmallFilterMode
          filters={
            <Box direction="row" fill>
              <Grid
                columns={
                  isPreviewMode
                    ? {
                        count: 3,
                        size: 'auto',
                      }
                    : ['220px', '220px', '220px', '220px']
                }
                gap="spacing12"
              >
                {!isPreviewMode && (
                  <SearchInput
                    testId="report"
                    placeholder="Search"
                    value={filtersValues.searchString}
                    disabled={!isEmployerIsPresent}
                    onChange={onSetFilterValue.searchString}
                  />
                )}

                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />
                <InvoiceIDInput
                  value={filtersValues.invoiceID}
                  disabled={!isEmployerIsPresent}
                  onChange={onSetFilterValue.invoiceID}
                />
                {!isPreviewMode && (
                  <DateRangeSelector
                    prefix="Date range"
                    startDate={filtersValues.startDate}
                    endDate={filtersValues.endDate}
                    onSetStartDate={onSetFilterValue.startDate}
                    onSetEndDate={onSetFilterValue.endDate}
                    disabled={!isEmployerIsPresent}
                  />
                )}
              </Grid>
            </Box>
          }
        >
          <Box>{childrenNode}</Box>
        </ReportTableWrapper>
      </TopButtonPortalWrapper>
    </Box>
  );
};
export default ReplenishmentInvoiceReportTable;
